/**
 * Textbausteine-Reiter auf /wissen (Punkt 9, Phase 2 Schritt 4,
 * docs/KONZEPT-VERSANDMODUS-UND-TEXTBAUSTEINE.md § 1.3/§ 4.6). Vierter Reiter neben „Was MAESTRO
 * weiß" / „Wie MAESTRO handelt" / „Woraus MAESTRO lernt" — verwaltet den betriebsweiten Pool aus
 * apps/api/src/routes/text-snippets.ts. Aufbau (Liste + Anlegen/Bearbeiten-Drawer) 1:1 nach dem
 * Baustein-CRUD in knowledge.tsx, aber schlanker: kein visibility-/Kunden-Feld, `category` als
 * einfaches Freitextfeld statt Auswahl (Konzept § 2.2: "weich, nicht hart" — additiv spaeter auf
 * eine feste Liste umstellbar, keine Vorabentscheidung noetig).
 *
 * Inaktive Bausteine bleiben HIER sichtbar (klar markiert) — anders als im TextbausteinPicker,
 * der nur aktive zum Einfuegen anbietet. „Löschen" ist ein Soft-Delete über PATCH { active:
 * false } (die Route hat bewusst kein DELETE, s. text-snippets.ts).
 */
import { useMemo, useState, type FormEvent } from "react";
import { useApi, apiMutate } from "../hooks/useApi";
import { Button, Card, Drawer, EmptyState, ErrorNote, Icon, Input, LoadingRows, Checkbox, Textarea } from "../ui/components";
import { useContentRefetch } from "../ui/CopilotProvider";

interface TextSnippet {
  id: string;
  title: string;
  content: string;
  category: string | null;
  active: boolean;
  updatedAt: string;
}

const EMPTY = { title: "", content: "", category: "", active: true };
type Form = typeof EMPTY;

const badge = "rounded-full px-2 py-0.5 font-label-md text-label-md";

export function TextbausteineTab() {
  const { data, loading, error, reload } = useApi<TextSnippet[]>("/api/text-snippets");
  useContentRefetch("text-snippets", () => reload());

  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState<Form>(EMPTY);
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const bausteine = useMemo(() => [...(data ?? [])].sort((a, b) => Date.parse(b.updatedAt) - Date.parse(a.updatedAt)), [data]);

  function openNew() {
    setEditId(null);
    setForm(EMPTY);
    setFormError(null);
    setOpen(true);
  }
  function openEdit(b: TextSnippet) {
    setEditId(b.id);
    setForm({ title: b.title, content: b.content, category: b.category ?? "", active: b.active });
    setFormError(null);
    setOpen(true);
  }

  async function save(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setBusy(true);
    setFormError(null);
    try {
      const body = {
        title: form.title,
        content: form.content,
        category: form.category || null,
        active: form.active,
      };
      if (editId) await apiMutate(`/api/text-snippets/${editId}`, "PATCH", body);
      else await apiMutate("/api/text-snippets", "POST", body);
      setOpen(false);
      reload();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  async function toggleActive(b: TextSnippet) {
    setBusy(true);
    setFormError(null);
    try {
      await apiMutate(`/api/text-snippets/${b.id}`, "PATCH", { active: !b.active });
      reload();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div className="mb-4 flex items-center justify-between gap-3">
        <p className="max-w-2xl font-body-sm text-body-sm text-on-surface-variant dark:text-secondary-fixed-dim">
          Wiederkehrende Textbausteine für Angebots-Mails, Kostenübernahme, Rechnung und Erinnerungen — betriebsweit, für alle Mitarbeiter abrufbar.
        </p>
        <Button type="button" className="shrink-0" onClick={openNew}>
          <Icon name="add" className="text-[18px]" /> Baustein hinzufügen
        </Button>
      </div>

      {error && <ErrorNote message={error} />}
      {formError && <ErrorNote message={formError} />}

      {loading ? (
        <Card className="overflow-hidden"><LoadingRows rows={3} /></Card>
      ) : bausteine.length === 0 ? (
        <Card className="overflow-hidden">
          <EmptyState
            icon="bookmark_add"
            title="Noch keine Textbausteine angelegt"
            hint="Legen Sie wiederkehrende Formulierungen an — sie stehen danach im Angebots-Builder, bei Kostenübernahme, Rechnung und Erinnerungen zum Einfügen bereit."
          />
        </Card>
      ) : (
        <div className="flex flex-col gap-3">
          {bausteine.map((b) => (
            <div key={b.id} className="rounded-2xl border border-outline-variant bg-surface-container-lowest px-4 py-3.5 dark:border-white/10 dark:bg-white/[0.04]">
              <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="font-body-md text-body-md font-semibold text-on-background dark:text-inverse-on-surface">{b.title}</p>
                    {b.category && <span className={`${badge} bg-secondary-container text-on-secondary-container`}>{b.category}</span>}
                    {!b.active && <span className={`${badge} bg-surface-container-high text-secondary dark:bg-white/[0.08]`}>inaktiv</span>}
                  </div>
                  <p className="mt-1 line-clamp-2 font-body-sm text-body-sm text-on-surface-variant">{b.content}</p>
                </div>
                <div className="flex shrink-0 items-center gap-3">
                  <button type="button" className="inline-flex items-center gap-1 font-label-md text-label-md text-primary hover:underline" onClick={() => openEdit(b)}>
                    <Icon name="edit" className="text-[16px]" /> Bearbeiten
                  </button>
                  {/* Punkt 129: Kopie als neuer, noch ungespeicherter Baustein — gespeichert wird erst per „Speichern“. */}
                  <button
                    type="button"
                    className="inline-flex items-center gap-1 font-label-md text-label-md text-on-surface-variant hover:underline dark:text-secondary-fixed-dim"
                    onClick={() => {
                      openNew();
                      setForm({ title: `${b.title} (Kopie)`, content: b.content, category: b.category ?? "", active: b.active });
                    }}
                  >
                    <Icon name="content_copy" className="text-[16px]" /> Duplizieren
                  </button>
                  <button
                    type="button"
                    className="inline-flex items-center gap-1 font-label-md text-label-md text-on-surface-variant hover:underline disabled:opacity-50 dark:text-secondary-fixed-dim"
                    disabled={busy}
                    onClick={() => void toggleActive(b)}
                  >
                    <Icon name={b.active ? "visibility_off" : "visibility"} className="text-[16px]" /> {b.active ? "Deaktivieren" : "Aktivieren"}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Drawer open={open} onClose={() => setOpen(false)} title={editId ? "Baustein bearbeiten" : "Baustein hinzufügen"}>
        <form className="space-y-4" onSubmit={(e) => void save(e)}>
          {formError && <ErrorNote message={formError} />}
          <label className="block">
            <span className="mb-1 block font-body-sm text-body-sm font-semibold text-on-surface dark:text-inverse-on-surface">Titel *</span>
            <Input required placeholder="z. B. Zahlungserinnerung freundlich" value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} />
          </label>
          <label className="block">
            <span className="mb-1 block font-body-sm text-body-sm font-semibold text-on-surface dark:text-inverse-on-surface">Inhalt *</span>
            <Textarea required rows={8} placeholder="Klartext — der Text, der beim Einfügen 1:1 übernommen wird …" value={form.content} onChange={(e) => setForm((f) => ({ ...f, content: e.target.value }))} />
          </label>
          <label className="block">
            <span className="mb-1 block font-body-sm text-body-sm font-semibold text-on-surface dark:text-inverse-on-surface">Kategorie (optional)</span>
            <Input placeholder="z. B. angebot, kostenuebernahme, rechnung, erinnerung, allgemein" value={form.category} onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))} />
            <span className="mt-1 block font-label-md text-label-md text-on-surface-variant">
              Nur eine Sortier-/Filterhilfe — ein Baustein bleibt an jeder Sende-Stelle abrufbar, unabhängig von der Kategorie.
            </span>
          </label>
          <label className="flex items-center gap-2 font-body-sm text-body-sm text-on-surface dark:text-inverse-on-surface">
            <Checkbox checked={form.active} onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked }))} /> Aktiv (im Picker auswählbar)
          </label>
          <div className="flex justify-end gap-3 pt-2">
            <Button type="button" variant="secondary" onClick={() => setOpen(false)}>Abbrechen</Button>
            <Button type="submit" disabled={busy}>{busy ? "Speichert …" : "Speichern"}</Button>
          </div>
        </form>
      </Drawer>
    </>
  );
}
